import { dbClient } from './awsClients';
import { ScanCommand, BatchWriteItemCommandInput, BatchWriteItemCommand, GetItemCommandInput, GetItemCommand, UpdateItemCommand, UpdateItemCommandInput, AttributeValue, QueryCommand } from '@aws-sdk/client-dynamodb';
import { DEVICE_CHANGE_LOG_TABLE, DEVICE_LIVE_DATA_TABLE, DEVICE_TABLE, HOSPITAL_BLOCK_TABLE, HOSPITAL_DEVICE_TABLE, INDIVIDUAL_DEVICE_TABLE, NURSE_DEVICE_TABLE, USER_INFO_TABLE } from '@constants/dynamoDBTableName';
import { v4 as uuidv4 } from 'uuid';
import { CREATED_AT, CREATED_BY, DB_DEVICE_ID, DB_DEVICE_NAME, DB_DEVICE_STATUS, DB_DEVICE_TYPE, DB_HOSPITAL_BLOCK_ID, DB_STATUS, DELETED_AT, DELETED_BY, HOSPITAL_ID, MODIFIED_AT, MODIFIED_BY, PATIENT_NAME,MEDICINE_NAME, TENANT_DB_ID, UUID } from '@constants/dynamoDBAttributes';
import { TENANT_ID_CREATED_AT_INDEX } from '@constants/constants';
import { capitalizeFirstLetter, formatDateDMY, generateUniqueTimestamp, getDataByDeviceId, getDataUsingDeviceId, getDataUsingTenantId, getDataUsingUserId } from './sharedUtils';
import { DEVICE_INFUSION_LIST, ANALYTICS_CHART } from '@constants/auth/Api';
import { API_KEY } from '@constants/AppConfig';
import { fetchAuthSession } from 'aws-amplify/auth';

interface RequestItems {
    [tableName: string]: {
        PutRequest?: {
            Item: { [key: string]: AttributeValue }
        }
    }[]
}

export interface DeviceCardDetail {
    id: string;
    nurse_id?: string;
    hasControl?: boolean;
    device_id?: string;
    device_name?: string;
    patient_name?: string;
    medicine_name?: string;
    assigned?: string;
    deviceStatus: string;
    infusion_rate: number;
    volume: number;
    volume_consumed: number;
    time_duration: number;
    battery_percentage: number;
    battery_status: string;
    T_L_Time: number;
    T_Qua: number;
    Sel_Qua: number;
    RPM: number;
    error_code: string;
    status: string;
    created_at: string;
    created_at_time: string;
    created_by: string;
    bed_no: string;
}

const formattedDateTime = generateUniqueTimestamp();

export async function getDeviceChangeLogDataUsingDeviceId(tableName: string, deviceId: string) {
    const params = {
        TableName: tableName,
        IndexName: 'device_id-created_at-index',
        KeyConditionExpression: 'device_id = :device_id',
        ExpressionAttributeValues: {
            ':device_id': { S: deviceId }
        },
        ScanIndexForward: false, // Descending order, so the latest item is first
    };

    try {
        const command = new QueryCommand(params);
        const response = await dbClient.send(command);
        return response.Items && response.Items.length > 0 ? response.Items[0] : null;
    }
    catch (error) {
        console.log('error', error);
        throw error;
    }
}

const infusionStatusMap: Record<string, string> = {
    "0": "Infusion Not Started",
    "1": "Infusion In Progress",
    "2": "Infusion Complete",
    "5":"Infusion Started"
  };

  const infusionErrorMap: Record<string, string> = {
    "1": "Value Error",
    "2": "Battery less than 15%",
    "4": "Empty Saline bottle",
    "8": "Bubble Detection",
    "16": "Motor Not Working",
    "32": "Over flow detection",
    "64": "No Flow detection",
    "128": "Flow paused"
  };

export async function individualDeviceCardList(tenant_id: string, pushNotificationToken: string) {
    try {

        const individualDeviceData = await getDataUsingTenantId(INDIVIDUAL_DEVICE_TABLE, tenant_id);
        if (!individualDeviceData.Items || individualDeviceData.Items.length === 0) {
            return []
        }

        const deviceIds = individualDeviceData.Items.filter(item => !item.deleted_at || item.deleted_at.S === '').map(item => item);

        if (deviceIds.length === 0) {
            return []
        }

        const deviceList = [];
        const deviceDetails: DeviceCardDetail[] = [];

        for (const deviceId of deviceIds) {
            const getItemParams = {
                TableName: DEVICE_TABLE,
                Key: {
                    'uuid': { S: deviceId.device_id?.S ? deviceId.device_id?.S : '' }
                }
            };

            const deviceData = await dbClient.send(new GetItemCommand(getItemParams));

            if (deviceData.Item && deviceData.Item.status?.S?.toLowerCase() === 'approved') {

                if (deviceData.Item && deviceData.Item?.uuid?.S) {
                    const deviceControlData = await getDataByDeviceId(DEVICE_LIVE_DATA_TABLE, deviceData.Item?.device_id?.S ? deviceData.Item?.device_id?.S : '');
                    const deviceChangeLogDatas = await getDeviceChangeLogDataUsingDeviceId(DEVICE_CHANGE_LOG_TABLE, deviceData.Item?.device_id?.S ? deviceData.Item?.device_id?.S : '');

                    if (deviceControlData) {

                        const deviceControl = deviceControlData;

                        deviceDetails.push({
                            id: deviceData.Item?.uuid?.S ? deviceData.Item?.uuid?.S : '',
                            device_id: deviceData.Item?.device_id?.S ? deviceData.Item?.device_id?.S : '',
                            device_name: capitalizeFirstLetter(deviceData.Item?.device_name?.S ? deviceData.Item?.device_name?.S : ''),
                            patient_name: capitalizeFirstLetter(deviceChangeLogDatas && deviceChangeLogDatas.patient_name?.S || ''),
                            medicine_name: capitalizeFirstLetter(deviceChangeLogDatas && deviceChangeLogDatas.medicine_name?.S || ''),
                            deviceStatus: capitalizeFirstLetter(deviceData.Item?.device_status?.S ? deviceData.Item?.device_status?.S : ''),
                            infusion_rate: deviceControl?.RPM?.N ? parseFloat(deviceControl?.RPM?.N) : 0,
                            volume: deviceControl?.Re_saline?.N ? parseFloat(deviceControl?.Re_saline?.N) : 0,
                            volume_consumed: deviceControl?.Consume?.N ? parseFloat(deviceControl?.Consume?.N) : 0,
                            time_duration: deviceControl?.Time?.N ? parseFloat(deviceControl?.Time?.N) : 0,
                            battery_percentage: deviceControl?.Battery?.N ? parseFloat(deviceControl?.Battery?.N) : 0,
                            battery_status: (deviceControl?.Battery_Status?.N || deviceControl?.Battery_status?.N || "0"),
                            T_L_Time: deviceChangeLogDatas && deviceChangeLogDatas.T_L_Time?.N ? parseFloat(deviceChangeLogDatas.T_L_Time?.N) : 0,
                            T_Qua: deviceChangeLogDatas && deviceChangeLogDatas.T_Qua?.N ? parseFloat(deviceChangeLogDatas.T_Qua?.N) : 0,
                            RPM: deviceChangeLogDatas && deviceChangeLogDatas.RPM?.N ? parseFloat(deviceChangeLogDatas.RPM?.N) : 0,
                            status: deviceControl && deviceControl.Infuse_state?.N
                                ? infusionStatusMap[deviceControl.Infuse_state.N] || "Unknown Status"
                                : "",
                            error_code: deviceControl && deviceControl.Error_Code?.N && deviceControl.Error_Code?.N != '0'
                                ? infusionErrorMap[deviceControl.Error_Code.N]
                                : "",
                            Sel_Qua: deviceControl && deviceControl.Sel_Qua?.N ? parseFloat(deviceControl.Sel_Qua?.N) : 0,
                            created_at: deviceData.Item?.created_at?.S ? formatDateDMY(deviceData.Item?.created_at?.S) : '',
                            created_at_time: deviceData.Item?.created_at?.S ? deviceData.Item?.created_at?.S : '',
                            created_by: deviceData.Item?.created_by?.S ? deviceData.Item?.created_by?.S : '',
                            bed_no: '',
                        });
                    }
                }
            } else {
                // Omit the device if not approved
                continue;
            }
        }

        return deviceDetails;

    } catch (error: any) {
        throw error;
    }
}

export async function nurseDeviceCardList(hospitalId: string, nurseId: string, tenant_id: string, pushNotificationToken: string) {
    try {

        const nurseDeviceData = await getDataUsingUserId(NURSE_DEVICE_TABLE, nurseId);
        if (!nurseDeviceData.Items || nurseDeviceData.Items.length === 0) {
            return []
        }

        const deviceList = [];
        const deviceDetails: DeviceCardDetail[] = [];

        for (const device of nurseDeviceData.Items) {
            const getItemParams = {
                TableName: 'devices',
                Key: {
                    'uuid': { S: device.device_id?.S ? device.device_id?.S : '' }
                }
            };
            const deviceData = await dbClient.send(new GetItemCommand(getItemParams));

            if (deviceData.Item && deviceData.Item.status?.S?.toLowerCase() === 'approved') {
                if (deviceData.Item && deviceData.Item?.uuid?.S) {
                    const deviceControlData = await getDataByDeviceId(DEVICE_LIVE_DATA_TABLE, deviceData.Item?.device_id?.S || '');
                    if (!deviceControlData) {
                        continue; // Skip this iteration and continue with the next item
                    }

                    const deviceChangeLogDatas = await getDeviceChangeLogDataUsingDeviceId(DEVICE_CHANGE_LOG_TABLE, deviceData.Item?.device_id?.S || '');

                    const nurseDeviceDatas = await getDataUsingDeviceId(NURSE_DEVICE_TABLE, deviceData.Item?.uuid?.S || '');
                    const nurseDevice = nurseDeviceDatas && nurseDeviceDatas.Items && nurseDeviceDatas.Items.length > 0 ? nurseDeviceDatas.Items.find(item => item.has_control?.BOOL === true) : '';
                    const nurseUserData = nurseDevice ? await getDataUsingUserId(USER_INFO_TABLE, nurseDevice.user_id?.S || '') : '';
                    const nurseUserItem = nurseUserData ? nurseUserData.Items && nurseUserData.Items.length > 0 ? nurseUserData.Items[0] : '' : '';
                    const nurseName = nurseUserItem ? nurseUserItem.name?.S : '';

                    if (deviceControlData) {

                        const deviceControl = deviceControlData;

                        deviceDetails.push({
                            id: deviceData.Item?.uuid?.S ? deviceData.Item?.uuid?.S : '',
                            nurse_id: nurseUserItem && nurseDevice && nurseDevice.has_control?.BOOL == true ? nurseDevice.user_id?.S : '',
                            hasControl: nurseDevice && nurseDevice.has_control?.BOOL == true ? true : false,
                            device_id: deviceData.Item?.device_id?.S ? deviceData.Item?.device_id?.S : '',
                            device_name: capitalizeFirstLetter(deviceData.Item?.device_name?.S ? deviceData.Item?.device_name?.S : ''),
                            patient_name: capitalizeFirstLetter(deviceChangeLogDatas && deviceChangeLogDatas.patient_name?.S || ''),
                            medicine_name: capitalizeFirstLetter(deviceChangeLogDatas && deviceChangeLogDatas.medicine_name?.S || ''),
                            assigned: nurseDevice && nurseDevice.has_control?.BOOL == true ? capitalizeFirstLetter(nurseName || '') : '',
                            deviceStatus: capitalizeFirstLetter(deviceData.Item?.device_status?.S ? deviceData.Item?.device_status?.S : ''),
                            infusion_rate: deviceControl?.RPM?.N ? parseFloat(deviceControl?.RPM?.N) : 0,
                            volume: deviceControl?.Re_saline?.N ? parseFloat(deviceControl?.Re_saline?.N) : 0,
                            volume_consumed: deviceControl?.Consume?.N ? parseFloat(deviceControl?.Consume?.N) : 0,
                            time_duration: deviceControl?.Time?.N ? parseFloat(deviceControl?.Time?.N) : 0,
                            battery_percentage: deviceControl?.Battery?.N ? parseFloat(deviceControl?.Battery?.N) : 0,
                            battery_status: (deviceControl?.Battery_Status?.N || deviceControl?.Battery_status?.N || "0"),
                            T_L_Time: deviceChangeLogDatas && deviceChangeLogDatas.T_L_Time?.N ? parseFloat(deviceChangeLogDatas.T_L_Time?.N) : 0,
                            T_Qua: deviceChangeLogDatas && deviceChangeLogDatas.T_Qua?.N ? parseFloat(deviceChangeLogDatas.T_Qua?.N) : 0,
                            RPM: deviceChangeLogDatas && deviceChangeLogDatas.RPM?.N ? parseFloat(deviceChangeLogDatas.RPM?.N) : 0,
                            status: deviceControl && deviceControl.Infuse_state?.N
                                ? infusionStatusMap[deviceControl.Infuse_state.N] || "Unknown Status"
                                : "",
                            error_code: deviceControl && deviceControl.Error_Code?.N && deviceControl.Error_Code?.N != '0'
                                ? infusionErrorMap[deviceControl.Error_Code.N]
                                : "",
                            Sel_Qua: deviceControl && deviceControl.Sel_Qua?.N ? parseFloat(deviceControl.Sel_Qua?.N) : 0,
                            created_at: deviceData.Item?.created_at?.S ? formatDateDMY(deviceData.Item?.created_at?.S) : '',
                            created_at_time: deviceData.Item?.created_at?.S ? deviceData.Item?.created_at?.S : '',
                            created_by: deviceData.Item?.created_by?.S ? deviceData.Item?.created_by?.S : '',
                            bed_no: deviceData.Item?.hospital_block_id?.S || '',
                        });
                    }
                }
            } else {
                // Omit the device if not approved
                continue;
            }
        }
        return deviceDetails;

    } catch (error) {
        throw error;
    }
}

interface Block {
    name: string;
    parent_id: string;
    uuid: string;
}

export async function hospitalBlockParentData(hospitalId: string) {
    const params = {
        TableName: 'hospital_block',
        IndexName: 'hospital_id-created_at-index',
        FilterExpression: 'deleted_at = :deleted_at AND #parent_id = :parent_id',
        KeyConditionExpression: 'hospital_id = :hospital_id',
        ExpressionAttributeValues: {
            ':hospital_id': { S: hospitalId },
            ':deleted_at': { S: '' },
            ':parent_id': { S: '' },
        },
        ExpressionAttributeNames: {
            '#parent_id': 'parent_id',
        },

    };


    try {
        const command = new QueryCommand(params);
        const response = await dbClient.send(command);

        if (response.Items) {
            // Transform response.Items to extract only required fields
            const blocks: Block[] = response.Items.map(item => ({
                name: item.name?.S || '',
                parent_id: item.parent_id?.S || '',
                uuid: item.uuid?.S || '',
            }));
            return blocks;
        } else {
            return [];
        }
    } catch (error) {
        console.error('Error scanning hospital info:', error);
        throw error;
    }
}


async function batchWriteCommand(requestItems: any, data: any) {
    try {
        const params: BatchWriteItemCommandInput = {
            RequestItems: requestItems,
        };
        const command = new BatchWriteItemCommand(params);
        const response = await dbClient.send(command);
        return response;
    } catch (error: any) {
        let errorMessage = 'An unexpected error occurred while writing to the database';

        switch (error.name) {
            case 'ProvisionedThroughputExceededException':
                errorMessage = 'Provisioned throughput exceeded';
                break;
            case 'ResourceNotFoundException':
                errorMessage = 'Resource not found';
                break;
            default:
                errorMessage = error.message || 'An unknown error occurred';
        }

        return errorMessage;
    }
}

export const checkDeviceIdExists = async (deviceId: string) => {
    const params = {
        TableName: DEVICE_TABLE,
        FilterExpression: '#device_id = :device_id',
        ExpressionAttributeNames: {
            "#device_id": "device_id",
        },
        ExpressionAttributeValues: {
            ':device_id': { S: deviceId },
        },

    };

    try {
        const data = await dbClient.send(new ScanCommand(params));
        if (data.Items && data.Items.length > 0) {
            return 'Device ID exists'; // Device ID exists
        } else {
            return 'Device ID does not exist'; // Device ID does not exist
        }
    } catch (err) {
        console.error('Error checking device ID:', err);
        throw err;
    }
};

export const getDeviceId = async (deviceId: string) => {
    const params = {
        TableName: DEVICE_TABLE,
        IndexName: 'device_id-created_at-index',
        KeyConditionExpression: 'device_id = :device_id',
        ExpressionAttributeValues: {
            ':device_id': { S: deviceId },
        },
    };

    try {
        const data = await dbClient.send(new QueryCommand(params));
        if (data.Items && data.Items.length > 0) {
            return data.Items[0]; // Device ID exists
        } else {
            return '';
        }
    } catch (err) {
        console.error('Error checking device ID:', err);
    }
};

export const getDeviceData = async (tableName: string, device_id: string) => {
    const params: GetItemCommandInput = {
        TableName: tableName,
        Key: {
            uuid: { S: device_id }
        }
    };

    try {
        const response = await dbClient.send(new GetItemCommand(params));
        
        if (!response.Item) {
          return [];
        }
        const items = Array.isArray(response.Item) ? response.Item : [response.Item];
        const deviceList: any[] = [];
        for (const data of items) {
            if (data.device_id?.S) {
                deviceList.push({
                        device_id: data.device_id?.S || '',
                        device_name: data.device_name?.S || '',
                        device_status: data.device_status?.S || '',
                        status: data.status?.S || '',
                    });
                
            }
        }
        return deviceList;
    } catch (err) {
        console.error('Error fetching device data:', err);
        throw err;
    }
};


export async function addDeviceData(data: any) {
    const {
        tenant_id,
        device_id,
        device_name,
        device_status,
        status,
        hospital_id,
        user_id,
        date,
        deviceUuid,
        hospital_block_id
    } = data;

    const hospitalDeviceUuid = uuidv4();

    let requestItems: RequestItems = {
        [DEVICE_TABLE]: [],
        [HOSPITAL_DEVICE_TABLE]: [],
    };


    // Adding general device item
    requestItems[DEVICE_TABLE].push({
        PutRequest: {
            Item: {
                [UUID]: { S: deviceUuid },
                [DB_DEVICE_ID]: { S: device_id },
                [DB_DEVICE_NAME]: { S: device_name },
                [DB_DEVICE_STATUS]: { S: 'inactive' },
                [DB_DEVICE_TYPE]: { S: '' },
                [DB_STATUS]: { S: status },
                [DB_HOSPITAL_BLOCK_ID]: { S: hospital_block_id },
                [CREATED_BY]: { S: user_id },
                [CREATED_AT]: { S: date },
                [MODIFIED_BY]: { S: user_id },
                [MODIFIED_AT]: { S: date },
                [DELETED_BY]: { S: '' },
                [DELETED_AT]: { S: '' }
            },
        },
    });

    requestItems[HOSPITAL_DEVICE_TABLE].push({
        PutRequest: {
            Item: {
                [UUID]: { S: hospitalDeviceUuid },
                [HOSPITAL_ID]: { S: hospital_id },
                [DB_DEVICE_ID]: { S: deviceUuid },
                [TENANT_DB_ID]: { S: tenant_id },
                [CREATED_BY]: { S: user_id },
                [CREATED_AT]: { S: date },
                [MODIFIED_BY]: { S: user_id },
                [MODIFIED_AT]: { S: date },
                [DELETED_BY]: { S: '' },
                [DELETED_AT]: { S: '' }
            },
        },
    });

    for (const tableName in requestItems) {
        const tableRequests = requestItems[tableName];
        while (tableRequests.length > 0) {
            const batch = tableRequests.splice(0, 25);
            const batchRequestItems = { [tableName]: batch };

            try {
                const response = await batchWriteCommand(batchRequestItems, data);
            } catch (error) {
                console.error(`Error writing batch to ${tableName}:`, error);
            }
        }
    }
}

export async function individualAddDeviceData(data: any) {
    const {
        tenant_id,
        device_id,
        device_name,
        device_status,
        status,
        hospital_id,
        user_id,
        date,
        deviceUuid,
        hospital_block_id
    } = data;

    const individualDeviceUuid = uuidv4();

    let requestItems: RequestItems = {
        [DEVICE_TABLE]: [],
        [INDIVIDUAL_DEVICE_TABLE]: [],
    };


    // Adding general device item
    requestItems[DEVICE_TABLE].push({
        PutRequest: {
            Item: {
                [UUID]: { S: deviceUuid },
                [DB_DEVICE_ID]: { S: device_id },
                [DB_DEVICE_NAME]: { S: device_name },
                [DB_DEVICE_STATUS]: { S: 'inactive' },
                [DB_DEVICE_TYPE]: { S: '' },
                [DB_STATUS]: { S: status },
                [DB_HOSPITAL_BLOCK_ID]: { S: '0' },
                [CREATED_BY]: { S: user_id },
                [CREATED_AT]: { S: date },
                [MODIFIED_BY]: { S: user_id },
                [MODIFIED_AT]: { S: date },
                [DELETED_BY]: { S: '' },
                [DELETED_AT]: { S: '' }
            },
        },
    });

    requestItems[INDIVIDUAL_DEVICE_TABLE].push({
        PutRequest: {
            Item: {
                [UUID]: { S: individualDeviceUuid },
                [DB_DEVICE_ID]: { S: deviceUuid },
                [TENANT_DB_ID]: { S: tenant_id },
                [CREATED_BY]: { S: user_id },
                [CREATED_AT]: { S: date },
                [MODIFIED_BY]: { S: user_id },
                [MODIFIED_AT]: { S: date },
                [PATIENT_NAME]: { S: '' },
                [MEDICINE_NAME]: { S: '' },
                [DELETED_BY]: { S: '' },
                [DELETED_AT]: { S: '' }
            },
        },
    });

    for (const tableName in requestItems) {
        const tableRequests = requestItems[tableName];
        while (tableRequests.length > 0) {
            const batch = tableRequests.splice(0, 25);
            const batchRequestItems = { [tableName]: batch };

            try {
                const response = await batchWriteCommand(batchRequestItems, data);
            } catch (error) {
                console.error(`Error writing batch to ${tableName}:`, error);
            }
        }
    }
}

export async function deviceDetailUpdate(tableName: string, id: string, tenant_id: string, deviceName: string, deviceId: string, deviceStatus: string, treeViewSelectedValue: string) {
    // Add default value if treeViewSelectedValue is empty or undefined
    const blockId = treeViewSelectedValue || '0';

    const attributeNames = {
        '#device_name': 'device_name',
        '#device_id': 'device_id',
        '#device_status': 'device_status',
        '#status': 'status',
        '#hospital_block_id': 'hospital_block_id',
        '#modified_by': 'modified_by',
        '#modified_at': 'modified_at',
    };

    const attributeValues = {
        ':device_name': { S: deviceName },
        ':device_id': { S: deviceId },
        ':device_status': { S: 'inactive' },
        ':status': { S: deviceStatus },
        ':hospital_block_id': { S: blockId }, // Use the blockId variable here
        ':modified_by': { S: tenant_id },
        ':modified_at': { S: formattedDateTime },
    };

    const updateExpression =
        'SET #device_name = :device_name, #device_id = :device_id, #device_status = :device_status, #status = :status, #hospital_block_id = :hospital_block_id, #modified_by = :modified_by, #modified_at = :modified_at';

    const input: UpdateItemCommandInput = {
        TableName: tableName, // Replace with your table name
        Key: {
            uuid: { S: id }, // Replace with your primary key
        },
        ExpressionAttributeNames: attributeNames,
        ExpressionAttributeValues: attributeValues,
        UpdateExpression: updateExpression,
        ReturnValues: "ALL_NEW"
    };

    const command = new UpdateItemCommand(input);

    try {
        const response = await dbClient.send(command);
    } catch (error: any) {
        console.error("Error updating item:", error);

        let errorMessage: string;

        switch (error.name) {
            case 'ConditionalCheckFailedException':
                errorMessage = 'Condition check failed';
                break;
            case 'ProvisionedThroughputExceededException':
                errorMessage = 'Throughput limit exceeded';
                break;
            case 'ResourceNotFoundException':
                errorMessage = 'Requested resource not found';
                break;
            case 'ItemCollectionSizeLimitExceededException':
                errorMessage = 'Item collection size limit exceeded';
                break;
            case 'TransactionConflictException':
                errorMessage = 'Transaction conflict occurred';
                break;
            case 'RequestLimitExceeded':
                errorMessage = 'Request limit exceeded';
                break;
            case 'InternalServerError':
                errorMessage = 'Internal server error occurred';
                break;
            default:
                errorMessage = `Unknown error: ${error.message}`;
        }

        throw errorMessage
    }
};


export const checkHospitalDeviceIdExists = async (deviceId: string, tenant_id: string) => {
    const params = {
        TableName: DEVICE_TABLE,
        IndexName: 'device_id-created_at-index',
        KeyConditionExpression: 'device_id = :device_id',
        ExpressionAttributeValues: {
            ':device_id': { S: deviceId },
        },
    };

    try {
        const command = new QueryCommand(params);
        const data = await dbClient.send(command);
        if (data.Items && data.Items.length > 0) {
            const device = data.Items[0];
            const res = await hospitalDeviceIdExists(device.uuid?.S ? device.uuid?.S : '')
            if (res && res.length != 0) {
                const data = res[0];
                if (data.tenant_id?.S === tenant_id) {
                    return 'Device ID exists';
                } else {
                    return 'Invalid Device ID';
                }
            }
            return 'Invalid Device ID';
        } else {
            return 'Device ID does not exist'; // Device ID does not exist
        }
    } catch (err) {
        console.error('Error checking device ID:', err);
        throw err;
    }
};

export const hospitalDeviceIdExists = async (device_uuid: string) => {
    const params = {
        TableName: 'hospital_device',
        IndexName: 'device_id-created_at-index',
        KeyConditionExpression: 'device_id = :device_id',
        ExpressionAttributeValues: {
            ':device_id': { S: device_uuid },
        },
    };

    try {
        const command = new QueryCommand(params);
        const data = await dbClient.send(command);
        if (data.Items && data.Items.length > 0) {
            return data.Items;
        } else {
            return [];
        }
    } catch (err) {
        console.error('Error checking device ID:', err);
        throw err;
    }
};

export const checkIndividualDeviceIdExists = async (deviceId: string, tenant_id: string) => {
    const params = {
        TableName: DEVICE_TABLE,
        IndexName: 'device_id-created_at-index',
        KeyConditionExpression: 'device_id = :device_id',
        ExpressionAttributeValues: {
            ':device_id': { S: deviceId },
        },
    };

    try {
        const command = new QueryCommand(params);
        const data = await dbClient.send(command);
        if (data.Items && data.Items.length > 0) {
            const device = data.Items[0];
            const res = await individualDeviceIdExists(device.uuid?.S ? device.uuid?.S : '')
            if (res && res.length != 0) {
                const data = res[0];
                if (data.tenant_id?.S === tenant_id) {
                    return 'Device ID exists';
                } else {
                    return 'Invalid Device ID';
                }
            }
            return 'Invalid Device ID';
        } else {
            return 'Device ID does not exist'; // Device ID does not exist
        }
    } catch (err) {
        console.error('Error checking device ID:', err);
        throw err;
    }
};

export const individualDeviceIdExists = async (device_uuid: string) => {
    const params = {
        TableName: 'individual_device',
        IndexName: 'device_id-created_at-index',
        KeyConditionExpression: 'device_id = :device_id',
        ExpressionAttributeValues: {
            ':device_id': { S: device_uuid },
        },
    };

    try {
        const command = new QueryCommand(params);
        const data = await dbClient.send(command);
        if (data.Items && data.Items.length > 0) {
            return data.Items;
        } else {
            return [];
        }
    } catch (err) {
        console.error('Error checking device ID:', err);
        throw err;
    }
};

interface TreeNode {
    id: string;
    name: string;
    children?: TreeNode[];
}

function buildTree(structuredData: any[]): TreeNode[] {
    const nodeMap: { [key: string]: TreeNode } = {};
    const rootNodes: TreeNode[] = [];

    // Initialize nodes
    structuredData.forEach(item => {
        nodeMap[item.uuid] = {
            id: item.uuid,
            name: item.name,
            children: [],
        };
    });

    // Build tree by assigning children to their parents
    structuredData.forEach(item => {
        if (item.parent_id) {
            nodeMap[item.parent_id]?.children?.push(nodeMap[item.uuid]);
        } else {
            // No parent means it's a root node
            rootNodes.push(nodeMap[item.uuid]);
        }
    });

    return rootNodes;
}

export async function fetchHospitalBlockData(tenantId: string) {
    const params = {
        TableName: HOSPITAL_BLOCK_TABLE,
        IndexName: TENANT_ID_CREATED_AT_INDEX,
        FilterExpression: 'deleted_at = :deleted_at',
        KeyConditionExpression: 'tenant_id = :tenant_id',
        ExpressionAttributeValues: {
            ':tenant_id': { S: tenantId },
            ':deleted_at': { S: '' },
        },

    };


    try {
        const command = new QueryCommand(params);
        const response = await dbClient.send(command);

        if (response.Items && response.Items.length != 0) {
            const structuredData = response.Items.map((item: any) => ({
                uuid: item.uuid.S,
                name: item.name.S,
                parent_id: item.parent_id.S || null,
            }));

            return buildTree(structuredData);
        } else {
            return [];
        }
    } catch (error) {
        console.error('Error scanning hospital Block:', error);
        throw error;
    }
}

export async function fetchDeviceInfusionListApi(accessToken?: string): Promise<DeviceCardDetail[]> {
    let token = accessToken;
    if (!token) {
        try {
            const session = await fetchAuthSession();
            token = session.tokens?.accessToken?.toString();
        } catch (e) {
            console.warn('Could not fetch auth session token:', e);
        }
    }

    if (!token) {
        throw new Error('Access token is missing');
    }

    const response = await fetch(DEVICE_INFUSION_LIST, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY,
            'access-token': token,
        },
        body: JSON.stringify({}),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API error with status ${response.status}`);
    }

    const data = await response.json();
    return (data.items || []) as DeviceCardDetail[];
}

export interface AnalyticsChartParams {
    period?: string;
    device_id?: string;
    start_date?: string;
    end_date?: string;
}

export interface AnalyticsChartResponse {
    activeDeviceCount?: number;
    inActiveDeviceCount?: number;
    nonFunctionalDeviceCount?: number;
    totalDevices?: number;
    chartData?: { [key: string]: number };
    deviceData?: { device_id: string; device_name: string }[];
    counts?: {
        active?: number;
        inactive?: number;
        nonFunctional?: number;
    };
    [key: string]: any;
}

export async function fetchAnalyticsChartApi(
    params?: AnalyticsChartParams,
    accessToken?: string
): Promise<AnalyticsChartResponse> {
    let token = accessToken;
    if (!token) {
        try {
            const session = await fetchAuthSession();
            token = session.tokens?.accessToken?.toString();
        } catch (e) {
            console.warn('Could not fetch auth session token:', e);
        }
    }

    if (!token) {
        throw new Error('Access token is missing');
    }

    const response = await fetch(ANALYTICS_CHART, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY,
            'access-token': token,
            'Authorization': token.startsWith('Bearer') ? token : `Bearer ${token}`,
        },
        body: JSON.stringify(params || {}),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API error with status ${response.status}`);
    }

    const data = await response.json();
    return data as AnalyticsChartResponse;
}